/**
 * useCopilotLogger
 *
 * React hook that builds a `CopilotLogger` bound to:
 *   • the active error client (Sentry) from `<ObservabilityProvider>`,
 *   • the active analytics client (PostHog) from the same,
 *   • the live correlation id from `<CopilotCorrelationProvider>`.
 *
 * Any host page or hook that needs to log calls this — never imports
 * `createCopilotLogger` directly. The factory exists for utility code
 * that can't take React hooks (e.g. the engine's pure-function
 * helpers) and is the implementation backbone for this hook.
 *
 * Usage:
 *
 *   const log = useCopilotLogger('CP:Host:CheckUp', {
 *     flowId: 'checkup',
 *     journeyId: activeInstance?.id,
 *   });
 *
 *   log.info('mount');
 *   log.warn('after-finalize.failed', { data: { error: err.message } });
 *   log.error('saveCareAssessment.failed', undefined, { error: err });
 */

import { useMemo } from 'react';

import { useObservabilityClients } from '@tonnie/observability';

import {
  createCopilotLogger,
  type CopilotLogContext,
  type CopilotLogger,
  type CopilotLogScope,
} from './logger';
import { useCopilotCorrelation } from './correlation-context';

export function useCopilotLogger(
  scope: CopilotLogScope,
  defaultContext?: CopilotLogContext
): CopilotLogger {
  const clients = useObservabilityClients();
  const errorClient = clients.errors;
  const analyticsClient = clients.analytics;
  const { correlationId } = useCopilotCorrelation();

  // Memoise so re-renders don't churn the logger identity. Default
  // context is shallow-merged into every log call, so keeping the
  // logger stable when only `correlationId` changes is harmless —
  // `correlationId` is read fresh from a ref via the SDK getter.
  return useMemo(
    () =>
      createCopilotLogger({
        scope,
        defaultContext: { ...defaultContext, correlationId },
        sinks: {
          errorClient: errorClient ?? undefined,
          trackEvent: analyticsClient
            ? (name, properties) => {
                // The analytics client enforces the typed
                // EventRegistry; we pass through. Calls that don't
                // match a registered event will be rejected at the
                // analytics layer.
                analyticsClient.track(name as never, properties as never);
              }
            : undefined,
        },
      }),
    // We intentionally omit `correlationId` from deps — the SDK
    // getter reads it dynamically. Including it would re-create the
    // logger on every action, churning the breadcrumb category and
    // confusing dev-tools.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, errorClient, analyticsClient, JSON.stringify(defaultContext ?? {})]
  );
}
